import { useEffect, useRef } from 'react'
import ReactPlayer from 'react-player'
import { BuildingFile } from 'types/building'

type Props = {
  file: BuildingFile
}

export default function ViewerVideo({ file }: Props) {
  /**
   * Determine MIME type for video based on file's mimeType or extension
   * Supports multiple common video formats
   */
  const getVideoType = (mimeType: string) => {
    // Map common extensions to mimeType if mimeType is not available
    const mimeTypeMap: { [key: string]: string } = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
      avi: 'video/x-msvideo',
      mov: 'video/quicktime',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      mkv: 'video/x-matroska',
      m4v: 'video/x-m4v',
      '3gp': 'video/3gpp'
    }

    // If mimeType is already available and starts with 'video/', use it
    if (mimeType && mimeType.startsWith('video/')) {
      return mimeType
    }

    // If not, try to map from extension
    const extension = file.extension?.toLowerCase()
    if (extension && mimeTypeMap[extension]) {
      return mimeTypeMap[extension]
    }

    // Fallback to mp4 if cannot determine
    return 'video/mp4'
  }

  const videoType = getVideoType(file.mimeType)
  /**
   * Create URL list with fallback for different video formats
   * ReactPlayer will try to play video in order of the list
   */
  const getVideoUrls = () => {
    const urls = [
      {
        src: file.s3Path,
        type: videoType
      }
    ]

    // For MOV files, try different approaches
    if (file.extension?.toLowerCase() === 'mov') {
      // Try with generic video type
      urls.push({
        src: file.s3Path,
        type: 'video/*'
      })

      // Try without type specification
      urls.push({
        src: file.s3Path,
        type: ''
      })
    }

    return urls
  }

  // Alternative approach: use direct URL for problematic formats
  const getDirectUrl = () => {
    return file.s3Path
  }

  // Use direct URL for MOV files as they can be problematic with type specification
  const shouldUseDirectUrl = file.extension?.toLowerCase() === 'mov'
  const videoUrl = shouldUseDirectUrl ? getDirectUrl() : getVideoUrls()

  const videoRef = useRef<HTMLVideoElement>(null)
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isMOV =
    file.extension?.toLowerCase() === 'mov' ||
    file.mimeType?.toLowerCase().includes('quicktime')

  // For MOV files on iOS, we'll use the native video player
  useEffect(() => {
    if (videoRef.current) {
      const playPromise = videoRef.current.play()

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Try to play with muted if autoplay fails
          if (videoRef.current) {
            videoRef.current.muted = true
            videoRef.current.play().catch(() => {
              // Silent catch for muted play
            })
          }
        })
      }
    }
  }, [])

  if (isIOS && isMOV) {
    return (
      <div className="relative m-auto w-[80vw] aspect-video bg-[#021120] flex items-center justify-center">
        <video
          ref={videoRef}
          className="w-full h-full"
          controls
          playsInline
          autoPlay
          muted
          preload="auto"
          style={{ objectFit: 'contain' }}
        >
          <source src={file.s3Path} type="video/mp4" />
          <source src={file.s3Path} type="video/quicktime" />
          <track kind="captions" src="" label="English" />
          お使いのブラウザはビデオタグをサポートしていません。
        </video>
      </div>
    )
  }

  return (
    <div className="relative m-auto w-[80vw] aspect-video bg-[#021120]">
      <ReactPlayer
        url={videoUrl}
        playing
        zIndex={5000}
        controls
        width="100%"
        height="100%"
        style={{ objectFit: 'contain' }}
        playsinline
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
              playsInline: true,
              webkitPlaysInline: true
            }
          }
        }}
      />
    </div>
  )
}
